import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import type { AppState } from './types';
import { StoreProvider, useActions, useStore } from './store';
import { I18nProvider, useT } from './i18n';
import { ThemeProvider } from './theme';
import { DndProvider } from './ui/dnd';
import { GuestHero, PanelResizer, Toolbar, useAutoTitles, useIsMobile, usePanelLayout } from './ui/shell';
import { LibraryPanel } from './ui/library';
import { VariantsBoard } from './ui/timeline';
import { ComparePanel } from './ui/compare';
import { SnapshotControls, useServerSync } from './sync';
import { PricesProvider } from './prices';
import { useTemplateFromQuery } from './templates';
import {
  AccountButton,
  AuthGate,
  AuthProvider,
  ImpersonationBar,
  ResetPage,
  VerifyBanner,
  VerifyPage,
  useAuth,
} from './auth';
import { AdminPage } from './admin';
import { PricingPage } from './pricing';
import { LifehacksPage } from './lifehacks';
import { AboutPage, HowItWorksPage, TemplatePage, TemplatesPage, templateIdFromPath } from './pages';
import { OnboardingButton, OnboardingProvider } from './onboarding';
import { SharedTripPage } from './share';
import { SupportWidget } from './support';
import { TripNameField, TripsPage, TripsProvider, useTrips } from './trips';
import { navigate, useRoute } from './router';
import { useDocumentMeta } from './seo';
import styles from './App.module.css';

/**
 * Кнопка сворачивания боковой панели. Стоит на внутренней границе своей панели
 * — там же, где ручка ресайза, — и едет вместе с этой границей.
 *
 * Раньше кнопки жили на рейках у самых краёв экрана: место у них было своё,
 * постоянное, но к панели они не относились. Отсюда и вопрос «что эта кнопка
 * прячет»: у края экрана рядом с ней ничего нет. На шве такого вопроса не
 * возникает — кнопка стоит вплотную к тому, что сворачивает, а две рейки
 * перестают отъедать ширину у канваса.
 *
 * Стрелка всегда показывает, куда уедет панель: влево — спрятать левую,
 * вправо — раскрыть.
 */
function PanelToggle({
  side,
  open,
  controls,
  label,
  onToggle,
}: {
  side: 'left' | 'right';
  open: boolean;
  controls: string;
  label: string;
  onToggle: () => void;
}) {
  const t = useT();
  // Базовая стрелка смотрит влево; развороты — поворотом, чтобы смена
  // направления анимировалась, а не подменяла картинку скачком.
  const pointsLeft = side === 'left' ? open : !open;
  return (
    <button
      type="button"
      className={`${styles.seamBtn} ${side === 'left' ? styles.seamLeft : styles.seamRight}`}
      aria-expanded={open}
      aria-controls={controls}
      aria-label={label}
      title={t(open ? 'app.panel.hide' : 'app.panel.show', { name: label.toLowerCase() })}
      onClick={onToggle}
    >
      <svg
        className={`${styles.seamIcon} ${pointsLeft ? '' : styles.seamIconFlipped}`}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <polyline points="15 5 8 12 15 19" />
      </svg>
    </button>
  );
}

function Workspace() {
  const state = useStore();
  const { replaceState } = useActions();
  const t = useT();
  const auth = useAuth();
  const trips = useTrips();
  const { query } = useRoute();

  // Стабильная ссылка: хук синхронизации использует её как зависимость.
  const adopt = useCallback(
    (next: AppState) => {
      replaceState(next);
    },
    [replaceState],
  );

  // Шаблон, заказанный со страницы /templates/<id>.
  useTemplateFromQuery(query, state, adopt, t);

  // Синхронизация — только для вошедших: у гостя данные живут в браузере.
  // Ждём и список поездок: пока неизвестно, какая открыта, запрос ушёл бы за
  // поездкой по умолчанию, а следом сразу за нужной — лишний круг и мигание.
  const sync = useServerSync(state, adopt, {
    enabled: auth.status === 'user' && trips.status === 'ready',
    sessionKey: auth.user?.id ?? null,
    tripId: trips.currentId,
    // Общую поездку перечитываем сами: соавтор правит её из своего браузера.
    poll: trips.collaborative,
    guestBackupLabel: t('sync.snapshot.beforeLogin'),
  });
  const panels = usePanelLayout();
  const isMobile = useIsMobile();

  // На телефоне панели не сворачиваются: они не отнимают ширину у канваса, а
  // стоят над и под ним отдельными полосами. Сохранённое состояние сворачивания
  // при этом не трогаем — вернувшись на десктоп, человек застанет свою раскладку.
  const libraryOpen = isMobile || panels.libraryOpen;
  const compareOpen = isMobile || panels.compareOpen;

  // ?signin=1 приводит сюда со страницы сброса пароля: форма входа открывается
  // сразу, а параметр из адреса убирается, чтобы не всплыть при обновлении.
  const wantsSignIn = query.get('signin') === '1';
  useEffect(() => {
    if (wantsSignIn) navigate('/', { replace: true });
  }, [wantsSignIn]);

  // Во вкладке — название открытой поездки: их бывает несколько, и в ряду
  // одинаковых вкладок различать их больше нечем.
  useDocumentMeta({ title: trips.current?.name.trim() || undefined });

  const libraryRef = useRef<HTMLElement>(null);
  const compareRef = useRef<HTMLElement>(null);
  const [resizing, setResizing] = useState(false);

  // Ширины по умолчанию живут в CSS (tokens.css, включая брейкпоинты), поэтому
  // стартовое значение для перетаскивания снимается с реального элемента —
  // иначе первый же захват прыгнул бы на захардкоженное число.
  const [measured, setMeasured] = useState({ library: 288, compare: 340 });
  useLayoutEffect(() => {
    const library = libraryRef.current?.getBoundingClientRect().width;
    const compare = compareRef.current?.getBoundingClientRect().width;
    setMeasured((prev) => ({
      library: panels.libraryOpen && library ? library : prev.library,
      compare: panels.compareOpen && compare ? compare : prev.compare,
    }));
  }, [panels.libraryOpen, panels.compareOpen, panels.libraryWidth, panels.compareWidth]);

  const mainClass = [
    styles.main,
    libraryOpen ? '' : styles.libHidden,
    compareOpen ? '' : styles.compareHidden,
    resizing ? styles.resizing : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Переменные проставляются только после ручного ресайза: пока их нет,
  // работают значения из tokens.css вместе с их медиазапросами. На телефоне
  // ширины панелей не участвуют вовсе — колонка там одна, во весь экран.
  const mainStyle: CSSProperties = {};
  if (!isMobile && panels.libraryWidth !== null) {
    (mainStyle as Record<string, string>)['--lib-w'] = `${panels.libraryWidth}px`;
  }
  if (!isMobile && panels.compareWidth !== null) {
    (mainStyle as Record<string, string>)['--compare-w'] = `${panels.compareWidth}px`;
  }

  // Актуализация цен живёт рядом с синхронизацией и по тем же правилам:
  // она про открытую поездку, поэтому провайдер стоит ниже стора, входа и
  // списка поездок — id открытой поездки берётся у них.
  return (
    <PricesProvider tripId={trips.currentId}>
      <div className={styles.app}>
        {/* Тур живёт только на этом экране: он рассказывает про библиотеку,
            варианты и итог. В полосе он первый в ряду контролов поездки — с него
            работу начинают, а не им её настраивают. */}
        {/* Аккаунт разложен по-разному на широком и узком экране, и это не
            прихоть раскладки. На широком это кнопка в полосе с попапом под ней
            (actions ниже). На узком всю мобильную шапку, включая сайдбар с
            собственной аккаунтной секцией, рисует сам Toolbar через
            MobileShellHeader — сюда, в actions, кнопка аккаунта для мобильного
            вида уже не попадает, и передавать её отдельным слотом незачем. */}
        <Toolbar
          // <TripNameField/> сама по себе truthy даже тогда, когда внутри
          // рендерится в null (нет открытой поездки — гость либо список ещё
          // грузится): это узел, а не строка, и Toolbar передал бы его дальше
          // как есть. На десктопе разницы нет — рядом со знаком просто пусто,
          // как и раньше, — а на узком экране MobileShellHeader получает в
          // title либо этот узел, либо честное отсутствие (undefined) и по
          // этому отличает «есть открытая поездка» от «нечего показывать»:
          // во втором случае вместо двухстрочной шапки с заголовком рисуется
          // однострочная (см. компонент). Сам Toolbar так решить не может: он
          // ничего не знает про список поездок (см. комментарий у пропа
          // tripName) — а здесь, в Workspace, trips.current уже под рукой.
          tripName={trips.current ? <TripNameField /> : undefined}
          lead={<OnboardingButton />}
          accountInitialSignIn={wantsSignIn}
          actions={
            <>
              <SnapshotControls sync={sync} onRestore={adopt} />
              {!isMobile && <AccountButton initialSignIn={wantsSignIn} />}
            </>
          }
        />
        <VerifyBanner />
        {/* Гость видит демо-поездку (store/seed.ts) — полоса объясняет, что это,
            и зовёт завести аккаунт; «Просто попробовать» убирает её насовсем. */}
        <GuestHero />

        {/* Свёрнутая панель остаётся в дереве: её колонка анимированно схлопывается
            в ноль, поиск в библиотеке и позиция скролла не теряются. inert убирает
            её из фокуса и из дерева доступности, пока она не видна.
            Кнопки сворачивания стоят на внутренних швах и лежат поверх сетки:
            внутри панели их срезал бы её overflow, когда та схлопнута в ноль. */}
        <main className={mainClass} style={mainStyle}>
          <PanelToggle
            side="left"
            open={panels.libraryOpen}
            controls="library-panel"
            label={t('library.title')}
            onToggle={panels.toggleLibrary}
          />

          <aside
            ref={libraryRef}
            id="library-panel"
            className={styles.library}
            aria-label={t('app.library')}
            inert={!libraryOpen}
          >
            <div className={styles.panelInner}>
              <LibraryPanel />
            </div>
            {panels.libraryOpen && !isMobile && (
              <PanelResizer
                side="left"
                label={t('library.title')}
                width={panels.libraryWidth ?? measured.library}
                onResize={panels.setLibraryWidth}
                onResizeStart={() => setResizing(true)}
                onResizeEnd={() => setResizing(false)}
              />
            )}
          </aside>

          <section className={styles.board} aria-label={t('app.board')}>
            <VariantsBoard />
          </section>

          <aside
            ref={compareRef}
            id="compare-panel"
            className={styles.compare}
            aria-label={t('app.compare')}
            inert={!compareOpen}
          >
            <div className={styles.panelInner}>
              <ComparePanel />
            </div>
            {panels.compareOpen && !isMobile && (
              <PanelResizer
                side="right"
                label={t('compare.title')}
                width={panels.compareWidth ?? measured.compare}
                onResize={panels.setCompareWidth}
                onResizeStart={() => setResizing(true)}
                onResizeEnd={() => setResizing(false)}
              />
            )}
          </aside>

          <PanelToggle
            side="right"
            open={panels.compareOpen}
            controls="compare-panel"
            label={t('compare.title')}
            onToggle={panels.toggleCompare}
          />
        </main>
      </div>
    </PricesProvider>
  );
}

/**
 * Экраны приложения. Планировщик — основной; остальные открываются по прямым
 * ссылкам (письма, админка, тарифы, поиск), поэтому им не нужны ни стор, ни dnd.
 */
function Routes() {
  const { path } = useRoute();
  if (path === '/reset') return <ResetPage />;
  if (path === '/verify') return <VerifyPage />;
  if (path === '/admin') return <AdminPage />;
  if (path === '/trips') return <TripsPage />;
  if (path === '/pricing') return <PricingPage />;
  if (path === '/lifehacks') return <LifehacksPage />;
  if (path === '/how-it-works') return <HowItWorksPage />;
  if (path === '/about') return <AboutPage />;
  if (path === '/templates') return <TemplatesPage />;
  if (path.startsWith('/s/')) return <SharedTripPage />;

  // Страница шаблона — только для тех, что есть в каталоге: по выдуманному
  // адресу открывается планировщик, как и по любому другому неизвестному.
  const templateId = templateIdFromPath(path);
  if (templateId) return <TemplatePage id={templateId} />;

  return (
    <StoreProvider>
      <DndProvider>
        <Workspace />
      </DndProvider>
    </StoreProvider>
  );
}

export function App() {
  // Подсказки при наведении на кнопки — один делегированный обработчик на всё
  // приложение, включая экраны без стора (см. ui/shell/autoTitle.ts).
  useAutoTitles();
  return (
    <I18nProvider>
      <ThemeProvider>
        {/* Онбординг стоит выше входа: тур запускает не только кнопка в полосе,
            но и форма регистрации — сразу после того, как аккаунт создан. */}
        <OnboardingProvider>
          <AuthProvider>
            <TripsProvider>
              {/* Полоса «от имени» стоит над экранами: она не относится ни к
                  одному из них и должна быть видна на всех. */}
              <ImpersonationBar />
              <Routes />
              {/* Форму входа может позвать любая заблокированная для гостя кнопка. */}
              <AuthGate />
              {/* Кнопка поддержки видна на всех экранах вошедшему пользователю. */}
              <SupportWidget />
            </TripsProvider>
          </AuthProvider>
        </OnboardingProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
